/*
 * NAOEVO — coverflow depth for the Promotion grid mobile slider.
 *
 * The slide nearest the middle of the viewport sits full size and in front; the
 * ones either side shrink, tilt away and tuck underneath it, tracking the scroll
 * position in real time so the size follows the customer's finger.
 *
 * It also makes the carousel genuinely endless. The slider library's own isLoop
 * only jumps from the last slide back to the first on a touch swipe - the DOM still
 * has a hard first and last, so the edge slides show empty space on one side. Here a
 * clone of the last slide is prepended and a clone of the first appended, and once a
 * clone scrolls into the middle the scroll position is shifted silently onto its real
 * twin. There is always a neighbour either side.
 *
 * Measuring trap this avoids: the transform is applied to each slide's INNER
 * element, never to the slide box itself. getBoundingClientRect() reports
 * transformed geometry, so scaling the same element you measure feeds its new
 * size back into the next frame's maths and the slides visibly jitter. The outer
 * box stays untransformed, so every measurement is stable.
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 767px)';
  var mobileMQL = window.matchMedia(MOBILE_QUERY);
  var motionMQL = window.matchMedia('(prefers-reduced-motion: reduce)');
  var instances = [];

  function inner(slide) {
    return slide.firstElementChild || slide;
  }

  function Coverflow(container) {
    this.container = container;
    this.slides = Array.prototype.slice.call(container.children);
    this.minScale = (parseFloat(container.getAttribute('data-cf-scale')) || 86) / 100;
    this.tilt = parseFloat(container.getAttribute('data-cf-tilt'));
    if (isNaN(this.tilt)) this.tilt = 12;
    this.tuck = 0.1;
    this.loop = container.getAttribute('data-cf-loop') === 'true';
    this.realCount = this.slides.length;
    this.cloned = false;
    this.dots = [];
    this.idleTimer = null;
    this.active = false;
    this.onScroll = this.request.bind(this);
  }

  // Drawn straight from the scroll handler rather than through requestAnimationFrame.
  // Browsers already coalesce scroll events to one per frame, and a rAF id that never
  // gets serviced (backgrounded tab, a compositor that has stalled) would leave the
  // pending flag set and freeze the effect for good. Reading a handful of rects per
  // frame is cheap enough not to need the extra hop.
  Coverflow.prototype.request = function () {
    this.draw();
    this.syncDots();
    if (this.loop) {
      var self = this;
      if (this.idleTimer) clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(function () { self.wrapIfNeeded(); self.syncDots(); }, 140);
    }
  };

  Coverflow.prototype.draw = function () {
    var box = this.container.getBoundingClientRect();
    var middle = box.left + box.width / 2;

    for (var i = 0; i < this.slides.length; i++) {
      var slide = this.slides[i];
      var rect = slide.getBoundingClientRect();      // untransformed - see header note
      if (!rect.width) continue;

      // signed distance from the middle, in slide-widths
      var signed = (rect.left + rect.width / 2 - middle) / rect.width;
      var away = Math.min(Math.abs(signed), 1);
      var clamped = Math.max(Math.min(signed, 1), -1);

      var scale = 1 - away * (1 - this.minScale);
      var rotate = -clamped * this.tilt;
      var shift = -clamped * rect.width * this.tuck;   // pull neighbours under the middle one

      inner(slide).style.transform =
        'perspective(900px) translateX(' + shift.toFixed(2) + 'px) rotateY(' +
        rotate.toFixed(2) + 'deg) scale(' + scale.toFixed(4) + ')';
      slide.style.zIndex = String(100 - Math.round(away * 100));
      inner(slide).style.filter = away > 0.02 ? 'brightness(' + (1 - away * 0.16).toFixed(3) + ')' : '';
    }
  };


  // Endless: a clone of the last slide goes in front, a clone of the first goes after.
  Coverflow.prototype.addClones = function () {
    if (this.cloned || !this.loop || this.realCount < 2) return;
    var first = this.slides[0];
    var last = this.slides[this.realCount - 1];

    var head = last.cloneNode(true);
    var tail = first.cloneNode(true);
    [head, tail].forEach(function (node) {
      node.setAttribute('data-cf-clone', 'true');
      node.setAttribute('aria-hidden', 'true');
      node.querySelectorAll('a, button, input, [tabindex]').forEach(function (el) {
        el.setAttribute('tabindex', '-1');
      });
    });

    this.container.insertBefore(head, first);
    this.container.appendChild(tail);
    this.slides = Array.prototype.slice.call(this.container.children);
    this.cloned = true;
    this.shiftTo(1, 'auto');            // start on the first real slide
  };

  Coverflow.prototype.removeClones = function () {
    if (!this.cloned) return;
    var clones = this.container.querySelectorAll(':scope > [data-cf-clone="true"]');
    for (var i = 0; i < clones.length; i++) clones[i].remove();
    this.slides = Array.prototype.slice.call(this.container.children);
    this.cloned = false;
  };

  // Scroll by the delta between two slides rather than assigning scrollLeft, so the
  // same maths works under RTL where scrollLeft runs negative.
  Coverflow.prototype.shiftTo = function (index, behavior) {
    var target = this.slides[index];
    if (!target) return;
    var box = this.container.getBoundingClientRect();
    var rect = target.getBoundingClientRect();
    var delta = (rect.left + rect.width / 2) - (box.left + box.width / 2);
    if (Math.abs(delta) < 1) return;
    // scrollBy, not scrollLeft: with scroll-snap-type mandatory an assignment to
    // scrollLeft gets re-snapped away before it takes, while the scroll API lands
    // cleanly. A delta is also direction-agnostic, so RTL's negative scrollLeft
    // needs no special case.
    this.container.scrollBy({
      left: delta,
      behavior: behavior === 'smooth' ? 'smooth' : 'instant'
    });
  };

  Coverflow.prototype.middleIndex = function () {
    var box = this.container.getBoundingClientRect();
    var middle = box.left + box.width / 2;
    var best = 0, bestDist = Infinity;
    for (var i = 0; i < this.slides.length; i++) {
      var r = this.slides[i].getBoundingClientRect();
      if (!r.width) continue;
      var d = Math.abs(r.left + r.width / 2 - middle);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    return best;
  };

  // Once scrolling settles on a clone, hop to its real twin. Invisible because both
  // look identical and the jump is one slide-width with snapping switched off.
  Coverflow.prototype.wrapIfNeeded = function () {
    if (!this.cloned) return;
    var i = this.middleIndex();
    if (i === 0) this.shiftTo(this.realCount, 'auto');
    else if (i === this.slides.length - 1) this.shiftTo(1, 'auto');
    this.draw();
  };

  Coverflow.prototype.realIndex = function (i) {
    if (!this.cloned) return i;
    return (i - 1 + this.realCount) % this.realCount;
  };

  Coverflow.prototype.syncDots = function () {
    if (!this.dots.length) return;
    var active = this.realIndex(this.middleIndex());
    for (var i = 0; i < this.dots.length; i++) {
      this.dots[i].classList.toggle('active', i === active);
    }
  };

  Coverflow.prototype.bindDots = function () {
    var wrapper = this.container.parentElement
      && this.container.parentElement.querySelector(':scope > .promotion-dots');
    if (!wrapper) return;
    this.dots = Array.prototype.slice.call(wrapper.querySelectorAll('.slider-indicator'));
    var self = this;
    this.dots.forEach(function (dot, i) {
      if (dot.getAttribute('data-cf-bound')) return;
      dot.setAttribute('data-cf-bound', 'true');
      dot.addEventListener('click', function () {
        self.shiftTo(self.cloned ? i + 1 : i, 'smooth');
      });
    });
  };

  Coverflow.prototype.enable = function () {
    if (this.active) return;
    this.active = true;
    for (var i = 0; i < this.slides.length; i++) {
      var el = inner(this.slides[i]);
      el.style.transformOrigin = '50% 55%';
      el.style.willChange = 'transform';
      el.style.transition = 'filter .2s linear';
      this.slides[i].style.position = 'relative';
    }
    this.addClones();
    this.bindDots();
    this.container.addEventListener('scroll', this.onScroll, { passive: true });
    this.draw();
    this.syncDots();
  };

  Coverflow.prototype.disable = function () {
    if (!this.active) return;
    this.active = false;
    this.container.removeEventListener('scroll', this.onScroll);
    for (var i = 0; i < this.slides.length; i++) {
      var el = inner(this.slides[i]);
      el.style.transform = '';
      el.style.willChange = '';
      el.style.transition = '';
      el.style.filter = '';
      this.slides[i].style.zIndex = '';
    }
    if (this.idleTimer) { clearTimeout(this.idleTimer); this.idleTimer = null; }
    this.removeClones();
  };

  function boot() {
    var containers = document.querySelectorAll('[data-coverflow="true"] .slider-container');
    for (var i = 0; i < containers.length; i++) {
      if (containers[i].__coverflow) continue;
      var cf = new Coverflow(containers[i]);
      containers[i].__coverflow = cf;
      instances.push(cf);
    }
    apply();
  }

  function apply() {
    var mobile = mobileMQL.matches;
    var reduced = motionMQL.matches;
    for (var i = 0; i < instances.length; i++) {
      if (mobile && !reduced) instances[i].enable();
      else instances[i].disable();
    }
  }

  function refresh() {
    for (var i = 0; i < instances.length; i++) if (instances[i].active) instances[i].request();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.addEventListener('resize', function () { apply(); refresh(); }, { passive: true });
  if (mobileMQL.addEventListener) mobileMQL.addEventListener('change', apply);
  else if (mobileMQL.addListener) mobileMQL.addListener(apply);

  // Shopify theme editor re-renders sections in place
  document.addEventListener('shopify:section:load', boot);
})();
